'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement, type PostFactiiiReference, type SearchFactiii } from '@shared/all/domains/factiiis';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import Button from '../../common/Button';
import Modal, { ModalContent, ModalHeader } from '../../HOC/Modal';
import Input from '../../UI/Input';
type Props = {
  callback: (factiii?: PostFactiiiReference) => void;
  // What the link field asks for, when the factiii being tagged wants a
  // particular kind of link. "Enter URL" otherwise.
  urlLabel?: string;
};
export const SourceSearch = props => {
  const $ = _c(23);
  const [query, setQuery] = useState("");
  let t0;
  if ($[0] !== query) {
    t0 = {
      queryKey: ["SourceSearch", query],
      queryFn: () => unwrapFromResult(client.search.factiiis.query({
        query,
        requirements: [FactiiiRequirement.ANONYMOUS_SOURCE, FactiiiRequirement.HUMAN_SOURCE, FactiiiRequirement.ENTERPRISE_SOURCE, FactiiiRequirement.GOVERNMENT_SOURCE]
      }))
    };
    $[0] = query;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data,
    error,
    isLoading
  } = useQuery(t0);
  let t1;
  if ($[2] !== props) {
    t1 = () => props.callback();
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ModalHeader><p>Search Sources</p></ModalHeader>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => setQuery(e);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== query) {
    t4 = <Input className="mb-2 w-full rounded" value={query} label="Search for sources" onChange={t3} />;
    $[6] = query;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isLoading) {
    t5 = isLoading && <Loader2 className="absolute inset-y-0 right-6 top-6 animate-spin text-textSecondary/60" />;
    $[8] = isLoading;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== data?.factiiis || $[11] !== props) {
    t6 = data?.factiiis.map(factiii => <FactiiItem key={factiii.id} factiii={factiii} urlLabel={props.urlLabel} callback={factiii_0 => props.callback(factiii_0)} />);
    $[10] = data?.factiiis;
    $[11] = props;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== error) {
    t7 = error && <p className="p-4 text-base font-thin">An error occurred. Please try again later.</p>;
    $[13] = error;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <ModalContent><div className="space-y-4">{t4}{t5}{t6}{t7}</div></ModalContent>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t1 || $[21] !== t8) {
    t9 = <><Modal closeHandler={t1}>{t2}{t8}</Modal></>;
    $[20] = t1;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
const FactiiItem = props => {
  const $ = _c(30);
  const [data, setData] = useState("");
  const {
    getFactiiiId
  } = useFactiiis(client);
  let t0;
  if ($[0] !== props.factiii.avatarKey) {
    t0 = <Avatar spaceAvatarKey={props.factiii.avatarKey} size={40} />;
    $[0] = props.factiii.avatarKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.factiii.name) {
    t1 = <p>{props.factiii.name}</p>;
    $[2] = props.factiii.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `f/${props.factiii.slug}`;
  let t3;
  if ($[4] !== t2) {
    t3 = <p className="text-textSecondary/70">{t2}</p>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className="flex flex-row space-x-2">{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = props.urlLabel ?? "Enter URL";
  let t6;
  if ($[9] !== data || $[10] !== t5) {
    t6 = <Input onChange={setData} value={data} label={t5} inputClassName="!p-2 text-sm" />;
    $[9] = data;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== data || $[13] !== getFactiiiId || $[14] !== props) {
    t7 = async () => {
      const factiii = await getFactiiiId(props.factiii.id);
      if (factiii) {
        props.callback({
          uploads: [],
          factiii,
          spaceTimes: [],
          data,
          factiiiId: props.factiii.id
        });
      }
    };
    $[12] = data;
    $[13] = getFactiiiId;
    $[14] = props;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== data) {
    t8 = !data || !/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(data);
    $[16] = data;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <Button onClick={t7} disabled={t8} text="Add" />;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t9) {
    t10 = <div className="flex flex-row space-x-2">{t6}{t9}</div>;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t4) {
    t11 = <div className="flex-1 space-y-2">{t4}{t10}</div>;
    $[24] = t10;
    $[25] = t4;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t0 || $[28] !== t11) {
    t12 = <div className="flex cursor-pointer space-x-4 rounded border border-ds-border-standard p-2 hover:bg-background-light">{t0}{t11}</div>;
    $[27] = t0;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};